import { useQueryCache } from '@pinia/colada';
import { computed, getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue';

import { useSplitAcrossWeeks } from '@/composables/useSplitAcrossWeeks';
import type { SplitInputs } from '@/composables/useSplitAcrossWeeks';
import { useCapacityCalendarStore } from '@/store/capacityCalendarStore';
import logger from '@/services/logger';
import { markPoScheduled, unmarkPoScheduled } from '@/services/purchaseOrderService';
import { PURCHASE_ORDERS_QUERY_KEY, usePurchaseOrderStore } from '@/store/purchaseOrderStore';
import { useUndoToastStore } from '@/store/undoToastStore';
import { extractErrorMessage } from '@/utils/apiError';
import { generateBuckets, isPastWeekKey } from '@/utils/calendarBuckets';
import { isoWeekNumber } from '@/utils/isoWeek';
import type { CalendarBucket } from '@/utils/calendarBuckets';
import type { SplitBooking, SplitType, SplitWeek } from '@/utils/splitAcrossWeeks';

export interface DialogTypeRow {
  rowId: string;
  capacityTypeId: string;
  typeCode: string;
  typeLabel: string;
  hasPlan: boolean;
  remaining: number | null;
  alreadyScheduled: boolean;
  // The factor this week's planning capacity was already scaled by at import. Surfaced so the
  // dialog can say where it went: it lands in remaining, never in the PO's own quantities.
  capacityFactor: number | null;
}

// The distribute endpoint only accepts confirmed/locked POs into existing allocations.
const SCHEDULABLE_STATES = ['purchase', 'done'];

export const ADJUSTMENT_MIN = -50;
export const ADJUSTMENT_MAX = 50;
export const ADJUSTMENT_STEP = 5;
const FULL_PERCENT = 100;
const SEARCH_DEBOUNCE_MS = 300;
const MS_PER_WEEK = 604_800_000;

export type MarkStep = 'idle' | 'confirm';

// "W41–W42" for a run of consecutive weeks, "W41, W43" when there is a gap.
function weekSpanLabel(weekStarts: string[]): string {
  const sorted = [...new Set(weekStarts)].sort();
  const labels = sorted.map((week) => `W${isoWeekNumber(week)}`);
  const consecutive = sorted.every((week, index) => index === 0 || Date.parse(week) - Date.parse(sorted[index - 1]) === MS_PER_WEEK);
  return consecutive && labels.length > 1 ? `${labels[0]}–${labels[labels.length - 1]}` : labels.join(', ');
}

export const DIALOG_FIELD = { VENDOR: 'vendor', WEEK: 'week', QUANTITIES: 'quantities', NOTES: 'notes', SPLIT: 'split' } as const;
export type DialogField = (typeof DIALOG_FIELD)[keyof typeof DIALOG_FIELD];
export interface DialogFieldError {
  field: DialogField;
  message: string;
}

export function useScheduleDialog(onScheduled?: () => void) {
  const calendarStore = useCapacityCalendarStore();
  const poStore = usePurchaseOrderStore();
  const queryCache = useQueryCache();
  const undoToast = useUndoToastStore();

  const open = ref(false);
  const vendorId = ref<string | null>(null);
  const weekKey = ref<string | null>(null);
  const poNumber = ref<string | null>(null);
  const quantities = ref<Record<string, number>>({});
  const adjustment = ref(0);
  const detachedTypeIds = ref<Set<string>>(new Set());
  const notes = ref('');
  const submitting = ref(false);
  const pendingOverbook = ref(false);
  const paneTab = ref<'split' | 'lines' | null>(null);
  // The plan quantities the split divides. Frozen when the split opens so the left form and the
  // pane never disagree about the totals; closing the split thaws them.
  const frozenTotals = ref<Record<string, number>>({});
  const markStep = ref<MarkStep>('idle');
  const markError = ref<string | null>(null);
  const marking = ref(false);
  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  // Work already done cannot be planned differently, so a week that has ended is not offered.
  // The current week stays: it is being worked now.
  const weekBuckets: CalendarBucket[] = generateBuckets('week').filter((bucket) => !isPastWeekKey(bucket.key));

  const vendorOptions = computed(() =>
    calendarStore.rows
      .filter((row) => row.isFirstInVendorGroup && row.vendorId && !row.isGhost)
      .map((row) => ({ vendorId: row.vendorId as string, vendorCode: row.vendorCode, vendorName: row.vendorName })),
  );

  const selectedPo = computed(() => poStore.items.find((po) => po.poNumber === poNumber.value) ?? null);

  const typeRows = computed<DialogTypeRow[]>(() => {
    const week = weekKey.value;
    if (!vendorId.value || !week) return [];
    return calendarStore.rows
      .filter((row) => row.vendorId === vendorId.value && !row.isGhost && row.capacityTypeId)
      .map((row) => {
        const allocation = calendarStore.allocations[row.id]?.[week];
        const alreadyScheduled = (selectedPo.value?.assignments ?? []).some(
          (assignment) => assignment.capacityTypeId === row.capacityTypeId && assignment.weekStart === week,
        );
        return {
          rowId: row.id,
          capacityTypeId: row.capacityTypeId as string,
          typeCode: row.typeCode,
          typeLabel: row.typeLabel,
          hasPlan: Boolean(allocation),
          remaining: allocation ? allocation.remainedCapacity : null,
          alreadyScheduled,
          capacityFactor: allocation?.capacityFactor ?? null,
        };
      });
  });

  // "Calculated" is the backend-derived per-type count: remaining when the backend could
  // compute it, else the full termination total (unknown-count POs still get a starting point).
  const calculatedByTypeId = computed<Record<string, number>>(() =>
    Object.fromEntries(
      typeRows.value.map((row) => {
        const po = selectedPo.value;
        const calculated = po
          ? po.remainingTotals
            ? Math.max(po.remainingTotals[row.typeCode] ?? 0, 0)
            : (po.terminationTotals[row.typeCode] ?? 0)
          : 0;
        return [row.capacityTypeId, calculated];
      }),
    ),
  );

  const schedulablePos = computed(() =>
    poStore.items.filter((po) => {
      if (!po.isCapacityRelevant) return false;
      if (po.remainingTotals === null) return true;
      return Object.values(po.remainingTotals).some((remaining) => remaining > 0);
    }),
  );

  const submittableRows = computed(() => typeRows.value.filter((row) => row.hasPlan && !row.alreadyScheduled));

  const overbookedTypes = computed(() =>
    submittableRows.value
      .map((row) => ({ typeCode: row.typeCode, by: (quantities.value[row.capacityTypeId] ?? 0) - (row.remaining ?? 0) }))
      .filter((entry) => entry.by > 0),
  );

  const totalQuantity = computed(() =>
    submittableRows.value.reduce((sum, row) => sum + (quantities.value[row.capacityTypeId] ?? 0), 0),
  );

  // The figure the adjustment slider scales, totalled over the rows it can actually write —
  // shown beside the percentage so the slider's effect is legible without reading the rows.
  const calculatedTotal = computed(() =>
    submittableRows.value.reduce((sum, row) => sum + (calculatedByTypeId.value[row.capacityTypeId] ?? 0), 0),
  );

  const isManualMode = computed(() => poNumber.value === null);

  const splitOpen = computed(() => paneTab.value === 'split');

  const vendorTypeRows = computed(() => calendarStore.rows.filter((row) => row.vendorId === vendorId.value && !row.isGhost && row.capacityTypeId));

  const showUncountableStrip = computed(() => selectedPo.value !== null && selectedPo.value.remainingTotals === null);

  const scheduledSummary = computed(() => {
    const assignments = selectedPo.value?.assignments ?? [];
    if (assignments.length === 0) return '';
    const types = vendorTypeRows.value
      .map((row) => ({
        code: row.typeCode,
        quantity: assignments.filter((assignment) => assignment.capacityTypeId === row.capacityTypeId).reduce((sum, assignment) => sum + assignment.quantity, 0),
      }))
      .filter((type) => type.quantity > 0)
      .map((type) => `${type.code} ${type.quantity.toLocaleString()}`);
    if (types.length === 0) return '';
    return `${types.join(' · ')} in ${weekSpanLabel(assignments.map((assignment) => assignment.weekStart))}`;
  });

  const canOpenSplit = computed(() => {
    if (!vendorId.value || !weekKey.value) return false;
    if (!typeRows.value.some((row) => row.hasPlan)) return false;
    if (isManualMode.value) return totalQuantity.value > 0 && notes.value.trim().length > 0;
    return poNumber.value !== null && totalQuantity.value > 0;
  });

  // Every loaded week from the first week onward, with per-type remaining capacity and the
  // existing blocks (grey bar segments). alreadyScheduled comes from the PO's own assignments.
  const splitWeeks = computed<SplitWeek[]>(() => {
    const firstIndex = weekBuckets.findIndex((bucket) => bucket.key === weekKey.value);
    if (firstIndex < 0) return [];
    return weekBuckets.slice(firstIndex).map((bucket) => {
      const remainingByType: Record<string, number | null> = {};
      const bookedByType: Record<string, SplitBooking[]> = {};
      vendorTypeRows.value.forEach((row) => {
        const allocation = calendarStore.allocations[row.id]?.[bucket.key];
        const typeId = row.capacityTypeId as string;
        remainingByType[typeId] = allocation ? allocation.remainedCapacity : null;
        bookedByType[typeId] = (allocation?.purchaseOrders ?? []).map((po) => ({ poNumber: po.poNumber, notes: po.notes ?? null, quantity: po.quantity }));
      });
      const alreadyScheduled = (selectedPo.value?.assignments ?? []).some((assignment) => assignment.weekStart === bucket.key);
      return { weekKey: bucket.key, label: bucket.label, remainingByType, alreadyScheduled, bookedByType };
    });
  });

  const splitTypes = computed<SplitType[]>(() =>
    vendorTypeRows.value.map((row) => ({ capacityTypeId: row.capacityTypeId as string, code: row.typeCode, total: frozenTotals.value[row.capacityTypeId as string] ?? 0 }))
  );

  // The calendar row behind each type — what the pane's "+ Add capacity" writes to, and
  // where the menu's capacity unit comes from (same field the board's no-plan cell passes).
  const splitRowIdByType = computed<Record<string, string>>(() => Object.fromEntries(vendorTypeRows.value.map((row) => [row.capacityTypeId as string, row.id])));
  const splitUnitByType = computed<Record<string, string | null>>(() => Object.fromEntries(vendorTypeRows.value.map((row) => [row.capacityTypeId as string, row.unit])));

  const splitInputs = computed<SplitInputs | null>(() => {
    if (!vendorId.value || !splitOpen.value) return null;
    return {
      vendorId: vendorId.value,
      poNumber: poNumber.value,
      notes: notes.value,
      types: splitTypes.value,
      weeks: splitWeeks.value,
      rowIdByType: splitRowIdByType.value,
      unitByType: splitUnitByType.value,
    };
  });

  const split = useSplitAcrossWeeks(
    () => splitInputs.value,
    () => {
      open.value = false;
      onScheduled?.();
    }
  );

  function openSplit(): void {
    if (!canOpenSplit.value) return;
    cancelMark();
    frozenTotals.value = Object.fromEntries(submittableRows.value.map((row) => [row.capacityTypeId, quantities.value[row.capacityTypeId] ?? 0]));
    pendingOverbook.value = false;
    paneTab.value = 'split';
    // A prior open may have left the split halted on a failed week; reset() clears that lock
    // before applyPreset() re-fills, otherwise the locked composable would silently no-op.
    split.reset();
    split.applyPreset('fill');
  }

  function openLineItems(): void {
    paneTab.value = 'lines';
  }

  function closePane(): void {
    paneTab.value = null;
    pendingOverbook.value = false;
    split.reset();
    frozenTotals.value = {};
  }

  // A new first week re-applies the selected preset from there; hand edits are discarded on
  // purpose (cheap to redo, and the old week set no longer applies). reset() first in case the
  // prior week's split was halted on a failed save — otherwise applyPreset() would be a no-op.
  watch(weekKey, () => {
    if (!splitOpen.value) return;
    const preset = split.preset.value;
    split.reset();
    split.applyPreset(preset);
  });

  const canSubmit = computed(() => {
    if (!vendorId.value || !weekKey.value || submitting.value || totalQuantity.value <= 0) return false;
    if (!isManualMode.value) return true;
    // Aug 18 decision: a manual block (no PO) requires a comment explaining it.
    return notes.value.trim().length > 0;
  });

  const showErrors = ref(false);
  // Bumped once per failed submit, and nothing else: the dialog moves focus to the first
  // invalid field per attempt, so fixing one field mid-typing must not steal the caret.
  const submitAttempts = ref(0);

  // Split mode yields the notes error too (a reservation note can be cleared after the split
  // opens, since canOpenSplit is only checked on open) ahead of the placed-quantity check.
  const validationErrors = computed<DialogFieldError[]>(() => {
    if (splitOpen.value) {
      if (isManualMode.value && notes.value.trim().length === 0) {
        return [{ field: DIALOG_FIELD.NOTES, message: 'Add a note that names the reservation' }];
      }
      if (split.placedTotal.value === 0) {
        return [{ field: DIALOG_FIELD.SPLIT, message: 'Place a quantity in at least one week' }];
      }
      return [];
    }
    const errors: DialogFieldError[] = [];
    if (!vendorId.value) errors.push({ field: DIALOG_FIELD.VENDOR, message: 'Choose a vendor' });
    if (!weekKey.value) errors.push({ field: DIALOG_FIELD.WEEK, message: 'Choose a week' });
    if (totalQuantity.value <= 0) {
      const message = typeRows.value.some((row) => row.hasPlan)
        ? 'Enter a quantity for at least one type'
        : 'Pick a week where this vendor has a plan';
      errors.push({ field: DIALOG_FIELD.QUANTITIES, message });
    }
    if (isManualMode.value && notes.value.trim().length === 0) {
      errors.push({ field: DIALOG_FIELD.NOTES, message: 'Add a note that names the reservation' });
    }
    return errors;
  });

  const visibleErrors = computed<DialogFieldError[]>(() => (showErrors.value ? validationErrors.value : []));

  function errorFor(field: DialogField): string | null {
    return visibleErrors.value.find((error) => error.field === field)?.message ?? null;
  }

  const firstInvalidField = computed<DialogField | null>(() => visibleErrors.value[0]?.field ?? null);

  function openDialog(prefill?: { vendorId?: string; weekKey?: string; poNumber?: string }): void {
    reset();
    open.value = true;
    if (prefill?.weekKey) weekKey.value = prefill.weekKey;
    if (!prefill?.vendorId) return;
    if (prefill.poNumber) {
      void selectVendorAndPo(prefill.vendorId, prefill.poNumber);
    } else {
      void selectVendor(prefill.vendorId);
    }
  }

  function close(): void {
    open.value = false;
    reset();
  }

  async function selectVendor(id: string, search = ''): Promise<void> {
    if (splitOpen.value) closePane();
    cancelPendingSearch();
    vendorId.value = id;
    poNumber.value = null;
    quantities.value = {};
    poStore.states = [...SCHEDULABLE_STATES];
    poStore.schedulableForType = undefined;
    poStore.search = search;
    await poStore.load(id);
  }

  // The queue can hand over a PO that is not among the 100 most recent the dropdown loads, so
  // the vendor load searches for that number instead of the empty default.
  async function selectVendorAndPo(id: string, number: string): Promise<void> {
    await selectVendor(id, number);
    // The dialog may have been closed, or reopened for a different vendor, while this vendor's
    // POs were loading (open card A, Esc, open card B — A's load must not select A's PO into B).
    if (!open.value || vendorId.value !== id) return;
    if (poStore.items.some((po) => po.poNumber === number)) selectPo(number);
  }

  // Odoo returns only the 100 most recent POs per vendor, so an older PO is never in the
  // loaded list — typing has to reach the server rather than filter what is already here.
  function searchPos(value: string): void {
    // Vuetify echoes the selected item's title back through @update:search; that is not a
    // new query, and reloading on it would strip the list down to the selection alone.
    if (value === poNumber.value) return;
    poStore.search = value;
    cancelPendingSearch();
    searchTimer = setTimeout(() => {
      if (vendorId.value) void poStore.load(vendorId.value);
    }, SEARCH_DEBOUNCE_MS);
  }

  function cancelPendingSearch(): void {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = null;
  }

  function applyAdjustmentPrefill(): void {
    const factor = 1 + adjustment.value / FULL_PERCENT;
    quantities.value = Object.fromEntries(
      typeRows.value.map((row) => {
        if (detachedTypeIds.value.has(row.capacityTypeId)) {
          return [row.capacityTypeId, quantities.value[row.capacityTypeId] ?? 0];
        }
        const calculated = calculatedByTypeId.value[row.capacityTypeId] ?? 0;
        const prefill = row.hasPlan && !row.alreadyScheduled && calculated > 0 ? Math.round(calculated * factor) : 0;
        return [row.capacityTypeId, prefill];
      }),
    );
  }

  function setAdjustment(value: number): void {
    adjustment.value = Math.min(ADJUSTMENT_MAX, Math.max(ADJUSTMENT_MIN, value));
    applyAdjustmentPrefill();
  }

  // Back to the plan the PO and week imply: the slider returns to 0 and hand edits are dropped.
  function prefillQuantities(): void {
    adjustment.value = 0;
    detachedTypeIds.value = new Set();
    applyAdjustmentPrefill();
  }

  function selectPo(number: string | null): void {
    // Only a *different* PO invalidates an open split; re-emitting the same one (the week
    // dropdown used to, and Vuetify can) must leave the pane where it is.
    if (splitOpen.value && number !== poNumber.value) closePane();
    if (number !== poNumber.value) cancelMark();
    poNumber.value = number;
    prefillQuantities();
  }

  // While the split is open the quantities are frozen totals and the watcher below re-applies
  // the preset from the new first week; only the single-week form re-prefills.
  function setWeek(key: string | null): void {
    weekKey.value = key;
    if (!splitOpen.value) prefillQuantities();
  }

  function setQuantity(capacityTypeId: string, value: number): void {
    detachedTypeIds.value = new Set(detachedTypeIds.value).add(capacityTypeId);
    quantities.value = { ...quantities.value, [capacityTypeId]: Number.isFinite(value) && value > 0 ? value : 0 };
  }

  async function requestSubmit(): Promise<void> {
    if (splitOpen.value) {
      if (split.locked.value) return;
      if (validationErrors.value.length > 0) {
        showErrors.value = true;
        submitAttempts.value += 1;
        return;
      }
      if (split.evaluation.value.over.length > 0) {
        pendingOverbook.value = true;
        return;
      }
      await split.save();
      return;
    }
    if (!canSubmit.value) {
      showErrors.value = true;
      submitAttempts.value += 1;
      return;
    }
    if (overbookedTypes.value.length > 0) {
      pendingOverbook.value = true;
      return;
    }
    await submitConfirmed();
  }

  async function submitConfirmed(): Promise<void> {
    pendingOverbook.value = false;
    if (splitOpen.value) {
      await split.save();
      return;
    }
    if (!vendorId.value || !weekKey.value) return;
    const week = weekKey.value;
    const trimmedNotes = notes.value.trim();

    if (poNumber.value) {
      const portions = submittableRows.value
        .filter((row) => (quantities.value[row.capacityTypeId] ?? 0) > 0)
        .map((row) => ({ capacityTypeId: row.capacityTypeId, quantity: quantities.value[row.capacityTypeId] }));
      if (portions.length === 0) return;
      submitting.value = true;
      try {
        const ok = await calendarStore.distributePurchaseOrder(
          vendorId.value,
          week,
          poNumber.value,
          portions,
          trimmedNotes.length > 0 ? trimmedNotes : undefined,
        );
        if (ok) {
          // Only hide the dialog here, not a full reset — callers (and tests) may still
          // read typeRows/quantities from the just-submitted state right after this resolves.
          open.value = false;
          onScheduled?.();
        }
      } finally {
        submitting.value = false;
      }
      return;
    }

    // One grouped write, like a PO distribution: the backend tags the rows with a shared
    // distributionGroupId so a multi-type manual block renders (and moves, and deletes)
    // as one block instead of one per capacity type.
    const portions = submittableRows.value
      .filter((row) => (quantities.value[row.capacityTypeId] ?? 0) > 0)
      .map((row) => ({ capacityTypeId: row.capacityTypeId, quantity: quantities.value[row.capacityTypeId] }));
    if (portions.length === 0) return;
    submitting.value = true;
    try {
      const ok = await calendarStore.distributePurchaseOrder(vendorId.value, week, null, portions, trimmedNotes);
      if (ok) {
        open.value = false;
        onScheduled?.();
      }
    } finally {
      submitting.value = false;
    }
  }

  function cancelOverbook(): void {
    pendingOverbook.value = false;
  }

  function openMarkConfirm(): void {
    markError.value = null;
    markStep.value = 'confirm';
  }

  function cancelMark(): void {
    markStep.value = 'idle';
    markError.value = null;
  }

  // Only a PO whose remaining can't be computed may be marked; the backend rejects the rest.
  const canMark = computed(() => selectedPo.value !== null && selectedPo.value.remainingTotals === null);

  function invalidatePurchaseOrders(): void {
    void queryCache.invalidateQueries({ key: [PURCHASE_ORDERS_QUERY_KEY] });
  }

  async function confirmMark(): Promise<void> {
    const number = poNumber.value;
    const vendor = vendorId.value;
    if (!number || !vendor || marking.value) return;
    const note = notes.value.trim();
    marking.value = true;
    markError.value = null;
    try {
      await markPoScheduled(number, vendor, note.length > 0 ? note : null);
    } catch (error) {
      markError.value = extractErrorMessage(error, "Couldn't mark the PO as fully scheduled");
      return;
    } finally {
      marking.value = false;
    }
    close();
    invalidatePurchaseOrders();
    // Closes over this mark's PO and vendor: by the time Undo is clicked the dialog may show another.
    // undo() is fired from the toast's click handler, so a failure is surfaced here, not thrown.
    undoToast.show(`${number} marked as fully scheduled`, async () => {
      try {
        await unmarkPoScheduled(number, vendor);
        invalidatePurchaseOrders();
      } catch (error) {
        logger.showError(`Couldn't undo: ${extractErrorMessage(error)}`);
      }
    });
  }

  async function unmarkSelected(): Promise<void> {
    const number = poNumber.value;
    const vendor = vendorId.value;
    if (!number || !vendor || marking.value) return;
    marking.value = true;
    markError.value = null;
    try {
      await unmarkPoScheduled(number, vendor);
      invalidatePurchaseOrders();
      // The dialog's list is a disabled query, which invalidation does not refetch; reload it so the mark clears.
      await poStore.load(vendor);
    } catch (error) {
      markError.value = extractErrorMessage(error, "Couldn't unmark the PO");
    } finally {
      marking.value = false;
    }
  }

  watch(notes, () => {
    markError.value = null;
  });

  function reset(): void {
    cancelPendingSearch();
    poStore.search = '';
    vendorId.value = null;
    weekKey.value = null;
    poNumber.value = null;
    quantities.value = {};
    adjustment.value = 0;
    detachedTypeIds.value = new Set();
    notes.value = '';
    pendingOverbook.value = false;
    paneTab.value = null;
    frozenTotals.value = {};
    showErrors.value = false;
    submitAttempts.value = 0;
    markStep.value = 'idle';
    markError.value = null;
    split.reset();
  }

  // Lifecycle hooks only bind inside a component's setup(); the unit tests call the
  // composable bare.
  if (getCurrentInstance()) {
    onBeforeUnmount(cancelPendingSearch);
  }

  return {
    open, vendorId, weekKey, poNumber, quantities, adjustment, notes, submitting, pendingOverbook,
    weekBuckets, vendorOptions, typeRows, calculatedByTypeId, schedulablePos, selectedPo, overbookedTypes,
    totalQuantity, calculatedTotal, canSubmit, isManualMode,
    paneTab, splitOpen, split, splitInputs, frozenTotals, canOpenSplit,
    validationErrors, showErrors, submitAttempts, visibleErrors, errorFor, firstInvalidField,
    openDialog, close, selectVendor, selectPo, setWeek, searchPos, setQuantity, setAdjustment, requestSubmit, submitConfirmed,
    cancelOverbook, openSplit, openLineItems, closePane,
    showUncountableStrip, scheduledSummary, markStep, markError, marking, canMark,
    openMarkConfirm, cancelMark, confirmMark, unmarkSelected,
  };
}
